import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export interface StandingDto {
  position: number;
  teamName: string;
  teamLogo: string | null;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  /** Punti netti (3·V + N − PEN). */
  points: number;
  /** Penalizzazione punti (CGS/NOIF FIGC). Intero >=0. */
  penalty: number;
  isOwnTeam: boolean;
}

export interface StandingRowInput {
  position: number;
  teamName: string;
  teamLogo?: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  penalty?: number;
  isOwnTeam?: boolean;
}

// T-285: competition/season non piu' gestiti dal BO — il backend usa la stagione
// corrente. Restano opzionali per compatibilita' di tipo.
export interface ReplaceStandingsInput {
  competition?: string;
  season?: string;
  rows: StandingRowInput[];
}

export interface StandingsResponse {
  data: StandingDto[];
  season: string;
  competition: string;
  /** ISO dell'ultimo aggiornamento RIUSCITO dalla fonte esterna, o null. */
  lastSyncedAt: string | null;
  /** true se la classifica non e' stata aggiornata dopo l'ultimo cron domenicale (19:00 Roma). */
  autoSyncMissed?: boolean;
  /** Widget Tuttocampo del girone (config backend STANDINGS_PUBLIC_WIDGET_URL). */
  widgetUrl?: string;
}

const QK = {
  list: () => ['standings'] as const,
};

/** Classifica della stagione corrente + meta (season/competition risolti dal backend). */
export function useStandings() {
  return useQuery({
    queryKey: QK.list(),
    queryFn: async () => {
      const res = await apiClient.get<StandingsResponse>('/standings');
      return res.data;
    },
  });
}

export function useReplaceStandings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReplaceStandingsInput) => {
      const res = await apiClient.put<StandingsResponse>('/standings', input);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list() }),
  });
}

/**
 * Widget classifica Tuttocampo. Lo scarica il BROWSER dell'admin (Tuttocampo espone
 * CORS `*`): gli IP di datacenter (EC2) sono bloccati, quelli dell'ufficio no.
 * L'URL del widget arriva dal backend (GET /standings -> widgetUrl).
 */

/** Aggiorna la classifica da Tuttocampo a richiesta — solo admin. Il backend parsa e salva. */
export function useSyncStandings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const meta =
        qc.getQueryData<StandingsResponse>(QK.list()) ??
        (await apiClient.get<StandingsResponse>('/standings')).data;
      if (!meta.widgetUrl) throw new Error('URL widget Tuttocampo non configurato sul backend');
      let html: string;
      try {
        const page = await fetch(meta.widgetUrl, { signal: AbortSignal.timeout(10_000) });
        if (!page.ok) throw new Error(`HTTP ${page.status}`);
        html = await page.text();
      } catch (e) {
        throw new Error(`Tuttocampo non raggiungibile dal browser (${(e as Error).message})`);
      }
      const res = await apiClient.post<{ data: StandingDto[] }>('/standings/sync/html', { html });
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list() }),
  });
}

/** Legge la classifica da uno screenshot (motore vision). NON salva: anteprima + avvisi. */
export function useStandingsScreenshotPreview() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post<{ data: StandingRowInput[]; warnings: string[] }>(
        '/standings/import/screenshot',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data;
    },
  });
}
